import { Dialog, DialogTitle, DialogContent, DialogActions, Grid, Button } from '@mui/material';
import IconCancel from '@mui/icons-material/Cancel';
import IconWarning from '@mui/icons-material/Warning';
import { useState } from 'react';
import { useNotify, useRefresh } from 'react-admin';
import SubscriptionHandler from '../../react-admin/providers/handlers/SubscriptionHandler';

const useWaiveBalance = ({ setShow, userId }: { setShow: (flag: boolean) => void; userId: string | undefined }) => {
  const [loading, setLoading] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();

  const close = () => {
    setLoading(false);
    setShow(false);
  };
  const waiveBalance = async () => {
    setLoading(true);
    if (userId !== undefined) {
      await SubscriptionHandler.waiveBalance(
        userId,
        (voidedCount: number) => {
          // The count is payments marked cancelled, not invoices voided in Stripe. Failed voids are
          // dropped server-side and never reported, and paymentIntent-backed rows are marked
          // cancelled without being voided at all. The balance is the only reliable confirmation.
          notify(
            `Voided ${voidedCount} outstanding payment(s). Verify the balance reaches zero — if it does not, some voids failed.`
          );
          refresh();
          close();
        },
        (e: any) => {
          notify(`Failed to waive balance`, { type: 'error' });
          notify(e.toString(), { type: 'error' });
          close();
        }
      ).catch((e: any) => {
        // onFailure has already notified — this stops the re-thrown rejection going unhandled, but
        // still logs, since a throw from onSuccess itself would otherwise vanish silently.
        console.error('waiveBalance:', e);
      });
    } else {
      close();
    }
  };

  return {
    waiveBalance,
    close,
    loading,
  };
};

export const WaiveBalanceAction = ({ wallet }: { wallet: any }) => {
  const [show, setShow] = useState(false);
  const { waiveBalance, close, loading } = useWaiveBalance({
    setShow,
    userId: wallet.id,
  });

  // A cancelled subscription leaves the wallet at `plan: null`, so fall back the same way GeneralTab does.
  const currency = wallet?.plan?.currency ?? 'GBP';
  const unpaidAmount = wallet?.balance?.unpaidAmount;
  const formattedUnpaidAmount =
    typeof unpaidAmount === 'number'
      ? new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(unpaidAmount)
      : 'unknown';

  return (
    <>
      <Button color="primary" onClick={() => setShow(true)}>
        Waive Balance
      </Button>
      <Dialog fullWidth open={show} onClose={setShow} aria-label="Waive balance">
        <DialogTitle>Waive balance</DialogTitle>
        <DialogContent>
          <Grid item xs={12}>
            <span>
              <b>Attention!</b>
              <br />
              <br />
              You are about to void ALL outstanding invoices for this customer &mdash; monthly fees and store purchases.
              <br />
              <br />
              Outstanding balance: <b>{formattedUnpaidAmount}</b>
              <br />
              <br />
              This cannot be undone. The customer's subscription is NOT cancelled &mdash; if it is still active, next
              month's fee will still be charged.
              <br />
              <br />
              The balance is recalculated in the background, so it may take a few seconds to update after this closes.
              <br />
              <br />
              Are you sure you want to proceed?
            </span>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={loading}>
            <IconCancel />
            <span>Close</span>
          </Button>
          <Button onClick={waiveBalance} disabled={loading} color="error">
            <IconWarning />
            <span>Waive balance</span>
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
